/**
 * TextContextMenu - MS Word-style right-click formatting menu
 * 
 * Provides contextual formatting options for selected text including:
 * - Font family, size, color
 * - Bold, italic, underline
 * - Highlighting colors
 * - Text alignment
 * - Quick page setup access
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  Palette, 
  Type, 
  Settings,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Highlighter
} from 'lucide-react';

export interface TextFormat {
  fontFamily?: string;
  fontSize?: number;
  fontColor?: string;
  backgroundColor?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  alignment?: 'left' | 'center' | 'right' | 'justify';
}

export interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  selectedText: string;
  currentFormat?: TextFormat;
  onClose: () => void;
  onFormatApply: (format: Partial<TextFormat>) => void;
  onPageSetup: () => void;
}

export const TextContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  position,
  selectedText,
  currentFormat = {},
  onClose,
  onFormatApply,
  onPageSetup
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'format' | 'highlight' | 'advanced'>('format');

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close menu on escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Quick format buttons
  const quickFormats = [
    {
      icon: Bold,
      label: 'Bold',
      active: currentFormat.bold,
      action: () => onFormatApply({ bold: !currentFormat.bold })
    },
    {
      icon: Italic,
      label: 'Italic', 
      active: currentFormat.italic,
      action: () => onFormatApply({ italic: !currentFormat.italic })
    },
    {
      icon: Underline,
      label: 'Underline',
      active: currentFormat.underline,
      action: () => onFormatApply({ underline: !currentFormat.underline })
    }
  ];

  // Highlight colors (MS Word style)
  const highlightColors = [
    { color: 'transparent', label: 'No Highlight' },
    { color: '#ffff00', label: 'Yellow' },
    { color: '#00ff00', label: 'Green' },
    { color: '#00ffff', label: 'Cyan' },
    { color: '#ff00ff', label: 'Magenta' },
    { color: '#0080ff', label: 'Blue' },
    { color: '#ff8000', label: 'Orange' },
    { color: '#ff0080', label: 'Pink' }
  ];

  // Text colors
  const textColors = [
    '#000000', '#333333', '#666666', '#999999',
    '#ff0000', '#00ff00', '#0000ff', '#ffff00',
    '#ff00ff', '#00ffff', '#ff8000', '#8000ff'
  ];

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg min-w-80"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        maxHeight: '400px',
        overflow: 'auto'
      }}
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Format Text
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            "{selectedText.length > 20 ? selectedText.slice(0, 20) + '...' : selectedText}"
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {[
          { id: 'format', label: 'Format', icon: Type },
          { id: 'highlight', label: 'Highlight', icon: Highlighter },
          { id: 'advanced', label: 'Advanced', icon: Settings }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as any)}
            className={`flex-1 flex items-center gap-2 p-2 text-sm transition-colors ${
              activeTab === id
                ? 'bg-lex-accent-soft text-lex-accent'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-3">
        {activeTab === 'format' && (
          <div className="space-y-4">
            {/* Quick Format Buttons */}
            <div className="flex gap-1">
              {quickFormats.map(({ icon: Icon, label, active, action }) => (
                <button
                  key={label}
                  onClick={action}
                  className={`p-2 rounded transition-colors ${
                    active
                      ? 'bg-lex-accent text-lex-accent-contrast'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>

            {/* Font Size */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Font Size
              </label>
              <select
                value={currentFormat.fontSize || 12}
                onChange={(e) => onFormatApply({ fontSize: parseInt(e.target.value) })}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {[8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72].map(size => (
                  <option key={size} value={size}>{size}pt</option>
                ))}
              </select>
            </div>

            {/* Text Color */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Text Color
              </label>
              <div className="grid grid-cols-6 gap-1">
                {textColors.map(color => (
                  <button
                    key={color}
                    onClick={() => onFormatApply({ fontColor: color })}
                    className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-colors"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'highlight' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Highlight Color
              </label>
              <div className="grid grid-cols-4 gap-2">
                {highlightColors.map(({ color, label }) => (
                  <button
                    key={color}
                    onClick={() => onFormatApply({ backgroundColor: color })}
                    className={`p-2 rounded border-2 transition-colors ${
                      currentFormat.backgroundColor === color
                        ? 'border-lex-accent'
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                    style={{ backgroundColor: color === 'transparent' ? '#ffffff' : color }}
                  >
                    <span className="text-xs text-gray-700">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {/* Text Alignment */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Alignment
              </label>
              <div className="flex gap-1">
                {[
                  { value: 'left', icon: AlignLeft, label: 'Left' },
                  { value: 'center', icon: AlignCenter, label: 'Center' },
                  { value: 'right', icon: AlignRight, label: 'Right' }
                ].map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    onClick={() => onFormatApply({ alignment: value as any })}
                    className={`p-2 rounded transition-colors ${
                      currentFormat.alignment === value
                        ? 'bg-lex-accent text-lex-accent-contrast'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title={label}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>

            {/* Page Setup Access */}
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  onPageSetup();
                  onClose();
                }}
                className="w-full flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors text-gray-700 dark:text-gray-300"
              >
                <Settings className="w-4 h-4" />
                Page Setup & Document Settings
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
